import * as React from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Breadcrumbs,
  Button,
  Container,
  CssBaseline,
  Toolbar,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Grid,
  Chip,
  Link,
} from "@mui/material";
import SubscriptionsIcon from "@mui/icons-material/Subscriptions";
import CodeIcon from "@mui/icons-material/Code";
import Footer from "../components/Footer";
import JsonDataModal from "../components/admin/JsonDataModal";
import Navigation from "../components/Navigation";
import SquarespacePageHeader from "../components/SquarespacePageHeader";
import { Link as RouterLink } from "react-router-dom";
import { useApi } from "../hooks/useApi";

export default function SquarespaceSubscriptionDetail() {
  const { id } = useParams();
  const api = useApi();
  const [subscription, setSubscription] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [jsonModalOpen, setJsonModalOpen] = React.useState(false);

  React.useEffect(() => {
    const fetchSubscription = async () => {
      setLoading(true);
      setError(null);
      try {
        const responseData = await api.get(`/admin/squarespace/subscriptions/${id}`);
        setSubscription(responseData.data || responseData);
      } catch (err) {
        console.error("Error fetching subscription:", err);
        setError(err.message || "Failed to fetch subscription");
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchSubscription();
    }
  }, [id, api]);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateString;
    }
  };

  const getStatusColor = (status) => {
    const s = (status || "").toUpperCase();
    switch (s) {
      case "ACTIVE":
        return "success";
      case "PAUSED":
        return "warning";
      case "CANCELLED":
      case "EXPIRED":
        return "error";
      default:
        return "default";
    }
  };

  return (
    <>
      <Box sx={{ display: "flex" }}>
        <CssBaseline />
        <Navigation />
        <Box
          component="main"
          sx={{
            backgroundColor: (theme) => theme.palette.background.default,
            flexGrow: 1,
            height: "100vh",
            display: "flex",
            flexDirection: "column",
            overflow: "auto",
          }}
        >
          <Toolbar />
          <Container maxWidth="lg" sx={{ mt: 2, mb: 4, flexGrow: 1 }}>
            <Box sx={{ mb: 2 }}>
              <Breadcrumbs aria-label="breadcrumb">
                <Typography
                  color="primary"
                  component={RouterLink}
                  to="/dashboard"
                  sx={{
                    textDecoration: "none",
                    cursor: "pointer",
                    fontWeight: 500,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Home
                </Typography>
                <Typography
                  color="primary"
                  component={RouterLink}
                  to="/dashboard/squarespace"
                  sx={{
                    textDecoration: "none",
                    cursor: "pointer",
                    fontWeight: 500,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Squarespace
                </Typography>
                <Typography
                  color="primary"
                  component={RouterLink}
                  to="/dashboard/squarespace/subscriptions"
                  sx={{
                    textDecoration: "none",
                    cursor: "pointer",
                    fontWeight: 500,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Subscriptions
                </Typography>
                <Typography color="text.primary" fontWeight={500}>
                  {subscription?.id || id}
                </Typography>
              </Breadcrumbs>
            </Box>

            <SquarespacePageHeader
              title="Subscription Details"
              description="View Squarespace subscription information"
              icon={<SubscriptionsIcon sx={{ color: "primary.main" }} />}
            >
              {subscription && (
                <Button
                  variant="outlined"
                  startIcon={<CodeIcon />}
                  onClick={() => setJsonModalOpen(true)}
                >
                  View JSON
                </Button>
              )}
            </SquarespacePageHeader>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                <CircularProgress />
              </Box>
            ) : subscription ? (
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom fontWeight="bold">
                        Subscription Information
                      </Typography>
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          ID
                        </Typography>
                        <Typography variant="body1" gutterBottom sx={{ fontFamily: "monospace", fontSize: "0.85rem" }}>
                          {subscription.id}
                        </Typography>
                      </Box>
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Product
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                          {subscription.productName || "—"}
                        </Typography>
                      </Box>
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Product Type
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                          {subscription.productType || "—"}
                        </Typography>
                      </Box>
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Product ID
                        </Typography>
                        <Typography variant="body1" gutterBottom sx={{ fontFamily: "monospace", fontSize: "0.85rem" }}>
                          {subscription.productId ? (
                            <Link
                              component={RouterLink}
                              to={`/dashboard/squarespace/prices/${subscription.productId}`}
                              sx={{ fontFamily: "monospace", fontSize: "0.85rem" }}
                            >
                              {subscription.productId}
                            </Link>
                          ) : (
                            "—"
                          )}
                        </Typography>
                      </Box>
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Frequency
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                          {subscription.frequency || "—"}
                        </Typography>
                      </Box>
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Status
                        </Typography>
                        <Chip
                          label={(subscription.resoldStatus || "—")
                            .charAt(0)
                            .toUpperCase() +
                            (subscription.resoldStatus || "")
                            .slice(1)
                            .toLowerCase()}
                          color={getStatusColor(subscription.resoldStatus)}
                          size="small"
                        />
                      </Box>
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Auto Renew
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                          {subscription.autoRenew ? "Yes" : "No"}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom fontWeight="bold">
                        References
                      </Typography>
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Customer ID
                        </Typography>
                        <Typography variant="body1" gutterBottom sx={{ fontFamily: "monospace", fontSize: "0.85rem" }}>
                          {subscription.customerId ? (
                            <Link
                              component={RouterLink}
                              to={`/dashboard/squarespace/customers/${subscription.customerId}`}
                              sx={{ fontFamily: "monospace", fontSize: "0.85rem" }}
                            >
                              {subscription.customerId}
                            </Link>
                          ) : (
                            "—"
                          )}
                        </Typography>
                      </Box>
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Website ID
                        </Typography>
                        <Typography variant="body1" gutterBottom sx={{ fontFamily: "monospace", fontSize: "0.85rem" }}>
                          {subscription.websiteId ? (
                            <Link
                              component={RouterLink}
                              to={`/dashboard/squarespace/websites/${subscription.websiteId}`}
                              sx={{ fontFamily: "monospace", fontSize: "0.85rem" }}
                            >
                              {subscription.websiteId}
                            </Link>
                          ) : (
                            "—"
                          )}
                        </Typography>
                      </Box>
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Expires On
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                          {formatDate(subscription.expiresOn)}
                        </Typography>
                      </Box>
                      {subscription.productUrl && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Product URL
                          </Typography>
                          <Typography variant="body1" gutterBottom sx={{ fontFamily: "monospace", fontSize: "0.8rem", wordBreak: "break-all" }}>
                            {subscription.productUrl}
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>

              </Grid>
            ) : (
              <Alert severity="warning">
                Subscription not found
              </Alert>
            )}
          </Container>
          <Footer />
        </Box>
      </Box>
      <JsonDataModal
        open={jsonModalOpen}
        onClose={() => setJsonModalOpen(false)}
        data={subscription}
        title={`Subscription JSON - ${subscription?.productName || subscription?.id || "Subscription"}`}
      />
    </>
  );
}
